/**
 * 結果画面で項目の「値の出どころ」を示すバッジを決定する（#3130）。
 *
 * 背景: 従来は is_ocr_target === false というだけで一律「手入力」と表示していたが、
 * マスタ照合・辞書変換の反映先（column_mappings の item_id）は自動で値が入るため、
 * 「手入力」では意味が合わなかった。
 *
 * 1項目が複数の役割を兼ねる場合があるため（例: マスタ照合の反映先が辞書変換のキーでもある）、
 * バッジが横に並ばないよう優先順位を付けて必ず1つだけ返す。
 */
export const ItemSourceBadge = {
  /** 他の項目の値から自動計算される（#3145） */
  Calculated: 'calculated',
  /** この項目の値でマスタを検索する（AI読取ONでも表示する） */
  MasterKey: 'masterKey',
  /** この項目の値で辞書を検索する（AI読取ONでも表示する） */
  DictionaryKey: 'dictionaryKey',
  /** マスタ照合の結果が自動で書き込まれる */
  MasterFilled: 'masterFilled',
  /** 辞書変換の結果が自動で書き込まれる */
  DictionaryFilled: 'dictionaryFilled',
  /** AI読取せず、設定した固定値が入る（#3073） */
  FixedValue: 'fixedValue',
  /** AI読取せず、自動入力もされないため人が入力する */
  ManualInput: 'manualInput',
} as const;

export type ItemSourceBadgeType = (typeof ItemSourceBadge)[keyof typeof ItemSourceBadge];

export type ResolveItemSourceBadgeParams = {
  /** 対象項目の item_id */
  itemId: string;
  /** 項目設定の AI読取フラグ。undefined はデフォルト true（OCR対象）として扱う */
  isOcrTarget?: boolean;
  /** 項目設定の固定値（#3073）。空文字・undefined は未設定として扱う */
  fixedValue?: string;
  /** 項目設定の計算フィールドフラグ（#3145）。undefined は通常項目として扱う */
  isCalculated?: boolean;
  /** マスタ照合の検索キー項目の item_id 群 */
  masterSearchItemIds: ReadonlySet<string>;
  /** 辞書変換の検索キー項目の item_id 群 */
  dictionarySearchItemIds: ReadonlySet<string>;
  /** マスタ照合の反映先項目の item_id 群 */
  masterMappingTargetIds: ReadonlySet<string>;
  /** 辞書変換の反映先項目の item_id 群 */
  dictionaryMappingTargetIds: ReadonlySet<string>;
};

/**
 * 表示するバッジを1つだけ返す。該当なし（通常のAI読取項目）は null。
 *
 * 優先順位: 計算 > 照合キー > 辞書キー > 照合反映 > 辞書変換 > 固定値 > 手入力
 * キーを反映先より優先するのは、検索の起点である役割のほうが利用者にとって重要なため。
 * 固定値を反映先より下に置くのは、照合・辞書が後から値を上書きするため。
 *
 * 企業設定でマスタ照合／辞書変換が OFF の場合は呼び出し側で各 Set が空になるため、
 * 自動的に「固定値」または「手入力」へ倒れる。
 */
export const resolveItemSourceBadge = ({
  itemId,
  isOcrTarget,
  fixedValue,
  isCalculated,
  masterSearchItemIds,
  dictionarySearchItemIds,
  masterMappingTargetIds,
  dictionaryMappingTargetIds,
}: ResolveItemSourceBadgeParams): ItemSourceBadgeType | null => {
  // 計算フィールドは値の作り方そのものを示すため最優先で表示する（#3145）。
  // なお計算フィールドはマスタ照合・辞書変換のマッピング先に選べないため、実際には他バッジと競合しない
  if (isCalculated) return ItemSourceBadge.Calculated;

  // 検索キーはAI読取ONでも表示する（値でマスタ・辞書を引く起点であることを示すため）
  if (masterSearchItemIds.has(itemId)) return ItemSourceBadge.MasterKey;
  if (dictionarySearchItemIds.has(itemId)) return ItemSourceBadge.DictionaryKey;

  // 反映先もAI読取ONで表示する。照合・辞書で値が上書きされる事実は
  // AI読取のON/OFFに関わらず示す必要があるため、AI読取OFF判定より先に評価する。
  if (masterMappingTargetIds.has(itemId)) return ItemSourceBadge.MasterFilled;
  if (dictionaryMappingTargetIds.has(itemId)) return ItemSourceBadge.DictionaryFilled;

  // AI読取ONの通常項目はバッジなし。
  // AI読取ONで固定値ありの項目も、読み取れれば読取値が入り常に固定値とは限らないためバッジは出さない。
  if (isOcrTarget !== false) return null;

  // ここに来るのはAI読取OFFかつ照合・辞書の反映先でもない項目。
  // 固定値があれば人が入力せずその値が入るため「固定値」、なければ従来どおり「手入力」（#3073）。
  return fixedValue ? ItemSourceBadge.FixedValue : ItemSourceBadge.ManualInput;
};

/** バッジの表示文言 */
export const itemSourceBadgeLabels: Record<ItemSourceBadgeType, string> = {
  [ItemSourceBadge.Calculated]: '計算',
  [ItemSourceBadge.MasterKey]: '照合キー',
  [ItemSourceBadge.DictionaryKey]: '辞書キー',
  [ItemSourceBadge.MasterFilled]: '照合反映',
  [ItemSourceBadge.DictionaryFilled]: '辞書変換',
  [ItemSourceBadge.FixedValue]: '固定値',
  [ItemSourceBadge.ManualInput]: '手入力',
};
